import { isAuthError, reportAuthFailure } from '@/shared/api/auth-failure';
import { unwrapResult, type ApiResult } from '@/shared/api/result';
import { clientEnv } from '@/shared/config/env';
import { currentLocale } from '@/shared/i18n/messages';
import { reportEvent } from '@/shared/monitoring/monitoring';
import { getDeviceId } from '@/shared/device/device-id';
import { createRequestId } from '@/shared/utils/request-id';
import { metaClickHeaders } from '@/shared/tracking/meta-pixel';
import { protectRequest } from './api-crypto';

export interface HttpRequestOptions<TBody = unknown> {
  authToken?: string;
  body?: TBody;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  method?: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';
  runtimeVersion?: string;
  signal?: AbortSignal;
}

export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

const normalizeBase = (baseUrl: string) => baseUrl.replace(/\/$/, '');

const buildUrl = (path: string) => `${normalizeBase(clientEnv.apiBaseUrl)}${path}`;

export const requestJson = async <TResponse, TBody = unknown>(
  path: string,
  options: HttpRequestOptions<TBody> = {},
): Promise<TResponse> => {
  const method = options.method ?? 'GET';
  const bodyText = options.body === undefined ? '' : JSON.stringify(options.body);
  const requestUrl = new URL(buildUrl(path), window.location.origin);
  const cryptoHeaders = await protectRequest({
    bodyText,
    method,
    path: requestUrl.pathname,
    query: requestUrl.search ? requestUrl.search.slice(1) : '',
  });

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': currentLocale.value,
    'X-App-Version': clientEnv.appVersion,
    'X-Build-Version': clientEnv.buildVersion,
    'X-Client-Type': clientEnv.clientType,
    'X-Device-Id': getDeviceId(),
    'X-Request-Id': createRequestId(),
    'X-Site-Id': clientEnv.siteId,
    ...metaClickHeaders(),
    ...cryptoHeaders.headers,
    ...options.headers,
  };

  if (bodyText) headers['Content-Type'] = 'application/json';
  if (options.authToken) headers.Authorization = `Bearer ${options.authToken}`;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.runtimeVersion) headers['X-Runtime-Version'] = options.runtimeVersion;

  const startedAt = performance.now();
  const response = await fetch(requestUrl, {
    body: cryptoHeaders.bodyText || undefined,
    headers,
    method,
    signal: options.signal,
  });
  const durationMs = Math.round(performance.now() - startedAt);

  reportEvent({
    context: {
      durationMs,
      path,
      status: response.status,
    },
    event: 'api.request.completed',
    level: response.ok ? 'debug' : 'warn',
  });

  if (!response.ok) {
    const httpError = new HttpError(response.status, `接口请求失败：${response.status}`);
    if (isAuthError(httpError)) reportAuthFailure(httpError);
    throw httpError;
  }

  const result = (await response.json()) as ApiResult<TResponse>;
  try {
    return unwrapResult(result);
  } catch (error) {
    if (isAuthError(error)) reportAuthFailure(error);
    throw error;
  }
};
